import { useState } from "react";
import { Mail, Trash2 } from "lucide-react";

export const fmtDay = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

const whenText = (l) => {
  const parts = [l.deliver_at && `Arrives ${fmtDay(l.deliver_at)}`, l.on_hard_day && "on your next hard day"].filter(Boolean);
  return parts.length === 2 ? `${parts[0]}, or ${parts[1]}` : parts[0].replace(/^on/, "Arrives on");
};

export const SealedLetter = ({ l, onCancel }) => {
  const [armed, setArmed] = useState(false);
  return (
    <div className="mm-card p-4 flex items-center gap-4" data-testid={`letter-sealed-${l.id}`}>
      <div className="relative w-14 h-10 rounded-md bg-[#F3EBDD] border border-[#E3D6BF] shrink-0" aria-hidden>
        <div className="absolute inset-x-0 top-0 h-5 border-b border-[#E3D6BF]" style={{ clipPath: "polygon(0 0,100% 0,50% 100%)", background: "#EADFCB" }} />
        <div className="mm-seal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Sealed on {fmtDay(l.created_at)}</div>
        <div className="text-xs text-mm-muted" data-testid={`letter-sealed-when-${l.id}`}>{whenText(l)}</div>
      </div>
      {armed ? (
        <div className="flex gap-3 text-xs shrink-0">
          <button onClick={() => onCancel(l.id)} className="text-mm-error font-semibold" data-testid={`letter-cancel-yes-${l.id}`}>Cancel letter</button>
          <button onClick={() => setArmed(false)} className="text-mm-muted" data-testid={`letter-cancel-no-${l.id}`}>Keep</button>
        </div>
      ) : (
        <button onClick={() => setArmed(true)} className="p-2 text-mm-muted hover:text-mm-error transition-colors" aria-label="Cancel letter" data-testid={`letter-cancel-${l.id}`}><Trash2 size={16} /></button>
      )}
    </div>
  );
};

export const ArrivedLetter = ({ l, onOpen }) => (
  <button onClick={() => onOpen(l)} className="mm-card p-4 flex items-center gap-4 w-full text-left hover:-translate-y-0.5 transition-transform" data-testid={`letter-arrived-${l.id}`}>
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${l.opened_at ? "bg-mm-primary/10 text-mm-primary" : "mm-gradient"}`}><Mail size={20} /></div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm">{l.opened_at ? "Letter" : "New letter"} from {fmtDay(l.created_at)}</div>
      <div className="text-xs text-mm-muted">Arrived {fmtDay(l.delivered_at)}{l.delivered_via === "hard_day" ? " on a hard day" : ""}{l.reply_to ? " · a reply" : ""}</div>
    </div>
    {!l.opened_at && <span className="text-xs font-semibold text-mm-primary shrink-0">Open</span>}
  </button>
);

export const LetterView = ({ l, onReply, onDelete, onClose }) => {
  const [armed, setArmed] = useState(false);
  return (
    <div className="mm-envelope-open" data-testid="letter-view">
      <div className="mm-paper rounded-3xl p-6 sm:p-8 shadow-sm border border-mm-primary/10">
        <div className="text-xs uppercase tracking-wider text-mm-primary font-semibold">A letter from your past self</div>
        <div className="text-sm text-[#7A6B90] mt-1">Written {fmtDay(l.created_at)} · arrived {fmtDay(l.delivered_at)}</div>
        {l.prompt && <p className="italic text-[#5B3F8F] mt-4">{l.prompt}</p>}
        <p className="whitespace-pre-wrap leading-8 mt-3 text-[16px] break-words [overflow-wrap:anywhere]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="letter-view-body">{l.body}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button className="mm-btn" onClick={() => onReply(l)} data-testid="letter-reply-btn">Write back to future me</button>
        <button className="mm-btn-ghost" onClick={onClose} data-testid="letter-view-close">Back to letters</button>
        <span className="flex-1" />
        {armed ? (
          <span className="flex items-center gap-3 text-sm">
            <button className="text-mm-error font-semibold" onClick={() => onDelete(l.id)} data-testid="letter-delete-yes">Delete letter</button>
            <button className="text-mm-muted" onClick={() => setArmed(false)} data-testid="letter-delete-no">Keep</button>
          </span>
        ) : <button className="text-sm text-mm-muted hover:text-mm-error" onClick={() => setArmed(true)} data-testid="letter-delete-btn">Delete</button>}
      </div>
    </div>
  );
};
